/**
 * HFDS — Headzflex Design System
 * Component: HF-009 Sticky Add to Bag
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Create a mobile sticky Add to Bag bar.
 * - Mirror the selected product variant and price.
 * - Submit the original HF-007 product form.
 * - Hide while the original Add to Bag button is visible.
 * - Reflect loading and unavailable states.
 */

(() => {
  "use strict";

  const ENGINE_SELECTOR =
    "[data-hf-purchase-engine]";

  const MAIN_BUTTON_SELECTOR =
    '[data-hf-action="add-to-cart"]';

  const VARIANT_PICKER_SELECTOR =
    "[data-hf-variant-picker]";

  const ROOT_CLASS = "hf-sticky-atc";

  class HFStickyATC {
    /**
     * @param {HTMLElement} engine
     */
    constructor(engine) {
      this.engine = engine;

      this.productId = String(
        engine.dataset.productId || ""
      );

      this.productTitle =
        engine.dataset.productTitle || "Product";

      this.formId =
        engine.dataset.productFormId || "";

      this.form = this.formId
        ? document.getElementById(this.formId)
        : null;

      this.mainButton = engine.querySelector(
        MAIN_BUTTON_SELECTOR
      );

      this.variantPicker = engine.querySelector(
        VARIANT_PICKER_SELECTOR
      );

      this.purchaseEngine =
        engine.hfPurchaseEngine || null;

      this.root = null;
      this.button = null;
      this.titleElement = null;
      this.variantElement = null;
      this.priceElement = null;
      this.imageElement = null;
      this.separatorElement = null;

      this.currentVariant = null;
      this.mainButtonVisible = true;
      this.observer = null;
      this.resizeObserver = null;

      this.handleStickyClick =
        this.handleStickyClick.bind(this);

      this.handleVariantChange =
        this.handleVariantChange.bind(this);

      this.handleCartAdded =
        this.handleCartAdded.bind(this);

      this.handleCartError =
        this.handleCartError.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Sticky ATC] HFDS core is unavailable."
        );

        return;
      }

      if (!(this.form instanceof HTMLFormElement)) {
        console.error(
          "[HFDS Sticky ATC] Product form was not found."
        );

        return;
      }

      if (!(this.mainButton instanceof HTMLElement)) {
        console.error(
          "[HFDS Sticky ATC] Main Add to Bag button was not found."
        );

        return;
      }

      this.create();
      this.resolveInitialVariant();
      this.observeMainButton();

      this.engine.addEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      this.engine.addEventListener(
        "hfds:cart:item-added",
        this.handleCartAdded
      );

      this.engine.addEventListener(
        "hfds:cart:add-error",
        this.handleCartError
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        { passive: true }
      );

      this.syncLoadingState();
      this.handleResize();

      this.engine.dataset.hfStickyInitialized =
        "true";
    }

    create() {
      const wrapper =
        document.createElement("aside");

      wrapper.className = ROOT_CLASS;

      wrapper.dataset.hfStickyAtc = "";
      wrapper.dataset.productId = this.productId;

      wrapper.setAttribute(
        "aria-label",
        "Quick product purchase"
      );

      wrapper.innerHTML = `
        <div class="hf-sticky-atc__inner">
          <div class="hf-sticky-atc__product">
            <img
              class="hf-sticky-atc__image"
              data-hf-sticky-image
              alt=""
              hidden
            >

            <div class="hf-sticky-atc__details">
              <span
                class="hf-sticky-atc__title"
                data-hf-sticky-title
              ></span>

              <span class="hf-sticky-atc__meta">
                <span
                  class="hf-sticky-atc__variant"
                  data-hf-sticky-variant
                ></span>

                <span
                  class="hf-sticky-atc__separator"
                  data-hf-sticky-separator
                  hidden
                  aria-hidden="true"
                >
                  ·
                </span>

                <span
                  class="hf-sticky-atc__price"
                  data-hf-sticky-price
                ></span>
              </span>
            </div>
          </div>

          <div class="hf-sticky-atc__action">
            <button
              class="
                hf-button
                hf-button--primary
                hf-button--large
                hf-sticky-atc__button
              "
              type="button"
              data-hf-sticky-submit
            >
              <span class="hf-button__content">
                <span
                  class="
                    hf-button__icon
                    hf-button__icon--before
                  "
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M6 8h12l1 12H5L6 8Z"></path>
                    <path d="M9 8a3 3 0 0 1 6 0"></path>
                  </svg>
                </span>

                <span class="hf-button__label">
                  Add to Bag
                </span>
              </span>

              <span
                class="hf-button__spinner"
                aria-hidden="true"
              ></span>
            </button>
          </div>

          <span
            class="hf-sticky-atc__status"
            data-hf-sticky-status
            role="status"
            aria-live="polite"
          ></span>
        </div>
      `;

      document.body.appendChild(wrapper);

      this.root = wrapper;

      this.button = wrapper.querySelector(
        "[data-hf-sticky-submit]"
      );

      this.titleElement = wrapper.querySelector(
        "[data-hf-sticky-title]"
      );

      this.variantElement = wrapper.querySelector(
        "[data-hf-sticky-variant]"
      );

      this.priceElement = wrapper.querySelector(
        "[data-hf-sticky-price]"
      );

      this.imageElement = wrapper.querySelector(
        "[data-hf-sticky-image]"
      );

      this.separatorElement = wrapper.querySelector(
        "[data-hf-sticky-separator]"
      );

      this.titleElement.textContent =
        this.productTitle;

      this.button.addEventListener(
        "click",
        this.handleStickyClick
      );
    }

    resolveInitialVariant() {
      const purchaseEngine =
        this.engine.hfPurchaseEngine ||
        this.purchaseEngine;

      if (purchaseEngine?.currentVariant) {
        this.updateVariant(
          purchaseEngine.currentVariant
        );

        return;
      }

      const picker =
        this.variantPicker?.hfVariantPicker;

      if (picker?.currentVariant) {
        this.updateVariant(
          picker.currentVariant
        );

        return;
      }

      const initialJSON = this.engine.querySelector(
        "[data-hf-initial-variant-json]"
      );

      const initialVariant =
        window.HFDS.getJSONScript(
          initialJSON,
          null
        );

      this.updateVariant(initialVariant);
    }

    /**
     * @param {CustomEvent} event
     */
    handleVariantChange(event) {
      const detail = event.detail || {};

      if (
        detail.productId &&
        String(detail.productId) !==
          this.productId
      ) {
        return;
      }

      this.updateVariant(
        detail.variant || null
      );
    }

    /**
     * @param {Object|null} variant
     */
    updateVariant(variant) {
      this.currentVariant = variant;

      const available =
        Boolean(variant) &&
        variant.available === true;

      this.root.classList.toggle(
        "is-unavailable",
        !available
      );

      this.button.disabled = false;

      this.button.setAttribute(
        "aria-disabled",
        "false"
      );

      const label = this.button.querySelector(
        ".hf-button__label"
      );

      if (!variant) {
        this.variantElement.textContent =
          "Choose size";

        this.priceElement.textContent = "";

        this.separatorElement.hidden = true;

        if (label) {
          label.textContent = "Choose Size";
        }

        return;
      }

      const variantTitle =
        variant.title &&
        variant.title !== "Default Title"
          ? variant.title
          : "";

      this.variantElement.textContent =
        variantTitle;

      this.priceElement.textContent =
        window.HFDS.formatMoney(
          Number(variant.price)
        );

      this.separatorElement.hidden =
        !variantTitle;

      if (available) {
        if (label) {
          label.textContent = "Add to Bag";
        }
      } else if (label) {
        label.textContent = "Choose Size";
      }

      this.updateImage(variant);
    }

    /**
     * @param {Object} variant
     */
    updateImage(variant) {
      if (!(this.imageElement instanceof HTMLImageElement)) {
        return;
      }

      const mediaId =
        variant?.featured_media_id;

      let image = null;

      if (mediaId) {
        const slide = document.querySelector(
          `[data-hf-gallery-slide]` +
          `[data-media-id="${CSS.escape(
            String(mediaId)
          )}"]`
        );

        image = slide?.querySelector("img");
      }

      if (!image) {
        image = document.querySelector(
          `[data-hf-product-gallery]` +
          `[data-product-id="${CSS.escape(
            this.productId
          )}"] ` +
          `[data-hf-gallery-slide] img`
        );
      }

      if (!(image instanceof HTMLImageElement)) {
        this.imageElement.hidden = true;
        return;
      }

      this.imageElement.src =
        image.currentSrc || image.src;

      this.imageElement.alt =
        image.alt || this.productTitle;

      this.imageElement.hidden = false;
    }

    observeMainButton() {
      this.observer =
        window.HFDS.createObserver(
          (entries) => {
            const entry = entries[0];

            if (!entry) {
              return;
            }

            this.mainButtonVisible =
              entry.isIntersecting;

            this.updateVisibility();
          },
          {
            threshold: 0.15
          }
        );

      this.observer?.observe(
        this.mainButton
      );
    }

    handleResize() {
      this.updateVisibility();
    }

    updateVisibility() {
      const desktop =
        window.matchMedia(
          "(min-width: 64rem)"
        ).matches;

      const shouldShow =
        !desktop &&
        !this.mainButtonVisible &&
        !document.querySelector(
          "dialog[open]"
        );

      this.root.classList.toggle(
        "is-visible",
        shouldShow
      );

      document.body.classList.toggle(
        "hf-sticky-atc-visible",
        shouldShow
      );
    }

    handleStickyClick() {
      if (
        this.root.classList.contains(
          "is-loading"
        )
      ) {
        return;
      }

      if (
        !this.currentVariant ||
        this.currentVariant.available !== true
      ) {
        this.scrollToVariantPicker();
        return;
      }

      if (
        typeof this.form.requestSubmit ===
        "function"
      ) {
        this.form.requestSubmit(
          this.mainButton
        );
      } else {
        this.mainButton.click();
      }

      window.HFDS.emit(
        "sticky-atc:click",
        {
          productId: this.productId,
          variant: this.currentVariant
        },
        this.root
      );

      this.syncLoadingState();
    }

    scrollToVariantPicker() {
      const target =
        this.variantPicker ||
        this.mainButton;

      target?.scrollIntoView({
        behavior:
          window.HFDS.prefersReducedMotion()
            ? "auto"
            : "smooth",
        block: "center"
      });

      const firstInput =
        this.variantPicker?.querySelector(
          "[data-hf-option-input]"
        );

      window.setTimeout(() => {
        firstInput?.focus();
      }, 350);

      window.HFDS.announce(
        "Choose an available size.",
        "polite"
      );
    }

    syncLoadingState() {
      const loading =
        this.mainButton?.getAttribute(
          "aria-busy"
        ) === "true";

      this.root.classList.toggle(
        "is-loading",
        loading
      );

      if (this.button) {
        this.button.setAttribute(
          "aria-busy",
          String(loading)
        );
      }
    }

    handleCartAdded() {
      this.root.classList.remove(
        "is-loading"
      );

      this.button?.setAttribute(
        "aria-busy",
        "false"
      );

      const label = this.button?.querySelector(
        ".hf-button__label"
      );

      if (label) {
        label.textContent = "Added";
      }

      window.setTimeout(() => {
        if (label) {
          label.textContent =
            this.currentVariant?.available
              ? "Add to Bag"
              : "Choose Size";
        }
      }, 1600);
    }

    handleCartError() {
      this.root.classList.remove(
        "is-loading"
      );

      this.button?.setAttribute(
        "aria-busy",
        "false"
      );
    }

    destroy() {
      this.button?.removeEventListener(
        "click",
        this.handleStickyClick
      );

      this.engine.removeEventListener(
        "hfds:variant:change",
        this.handleVariantChange
      );

      this.engine.removeEventListener(
        "hfds:cart:item-added",
        this.handleCartAdded
      );

      this.engine.removeEventListener(
        "hfds:cart:add-error",
        this.handleCartError
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      this.observer?.disconnect();
      this.resizeObserver?.disconnect();

      this.root?.remove();

      document.body.classList.remove(
        "hf-sticky-atc-visible"
      );

      delete this.engine.dataset
        .hfStickyInitialized;
    }
  }

  function initializeStickyATC(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ENGINE_SELECTOR, context)
      .forEach((engine) => {
        if (
          !(engine instanceof HTMLElement)
        ) {
          return;
        }

        if (
          engine.dataset
            .hfStickyInitialized === "true"
        ) {
          return;
        }

        engine.hfStickyATC =
          new HFStickyATC(engine);
      });
  }

  window.HFDS?.ready(() => {
    initializeStickyATC(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeStickyATC(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(
          ENGINE_SELECTOR,
          event.target
        )
        .forEach((engine) => {
          engine.hfStickyATC?.destroy();
        });
    }
  );

  window.HFDSStickyATC =
    Object.freeze({
      version: "1.0.0",
      initialize: initializeStickyATC
    });
})();